<template>
    <div class="block-vision-mission-careers-outer">
        <div ref="quotesContainer" class="block-vision-mission-careers-quotes">
            <div class="quote-panels" ref="panelsContainer">
                <!-- Special Panel Wrapper -->
                <div class="quote-panel-wrapper" style="height: 100vh; position: relative;">
                    <div class="quote-panel quote-panel-special">
                        <div class="bgimg parallax-hook"
                             :style="`background-image:url('${window.config.json.artifacts.generalBg}')`"
                        ></div>
                        <div class="capper">
                            <div class="capper-padded">
                                <div class="quote-flx">
                                    <div class="quote-c">
                                        <div class="quote-c2">
                                            <TtmotifIcon class="tt-motif-svg" aria-label="Insomniac" />
                                            <div class="caption" v-html="json.missionVisionTitle"></div>
                                            <h2 v-html="json.missionVisionBigText"></h2>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="quote-panel-pad"></div>
                <!-- Regular Panels Wrappers -->
                <div
                    v-for="(item, index) in json.visionMissionCareers"
                    :key="`quote-panel-wrapper-${index+1}`"
                    class="quote-panel-wrapper"
                    style="position: relative;"
                    :class="{ reverse: index % 2 !== 0 }"
                >
                    <div class="quote-panel">
                        <div class="capper">
                            <div class="capper-padded">
                                <div class="quote-flx-outer">
                                    <div class="quote-flx">
                                        <div class="quote-l">
                                            <div class="quote-l2">
                                                <h2>{{ item.title }}</h2>
                                            </div>
                                        </div>
                                        <div class="quote-r">
                                            <div class="quote-r1">
                                                <div class="quote-r2">
                                                    <div class="bg-panel"></div>
                                                    <img :alt="item?.visionMissionCareersImage?.[0]?.title" :src="item?.visionMissionCareersImage?.[0].url" />
                                                </div>
                                                <div class="caption">{{ gh.removeTags(item.caption) }}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="quote-panel-pad"></div>
                <!-- End Regular Panels Wrappers -->
            </div>
        </div>
    </div>
</template>

<script>
import gh from '@/helpers/gh';
import SplitText from '@/plugins/gsap/SplitText';
import { nextTick } from 'vue';
import TtmotifIcon from "@/components/svgs/Ttmotif.vue";

export default {
    name: "VisionMissionCareers",
    components: {TtmotifIcon},
    props: {
        json: {
            type: Object,
            required: true,
            default: null,
        },
    },
    data() {
        return {
            window:window,
            gh:gh,
            fixedScreenHeight: 0,
            lastResizeWidth: 0,
            ticking: false,
            isIOSSafari: false,
        };
    },
    mounted() {
        nextTick(() => {
            this.isIOSSafari = this.checkIOSSafari();
            this.fixedScreenHeight = window.innerHeight;
            this.lastResizeWidth = window.innerWidth;

            this.initSplitTexts();
            window.addEventListener("scroll", this.onScroll, { passive: true });
            window.addEventListener("resize", this.handleResize);

            // Trigger an initial update.
            this.onScroll();
        })
    },
    beforeUnmount() {
        window.removeEventListener("scroll", this.onScroll);
        window.removeEventListener("resize", this.handleResize);
    },
    methods: {

        checkIOSSafari() {
            const ua = window.navigator.userAgent;
            const isAppleMobile = /iP(ad|hone|od)/.test(ua) || (ua.includes('Macintosh') && window.navigator.maxTouchPoints > 1);
            if (!isAppleMobile) return false;
            if (!/Safari/.test(ua)) return false;
            if (/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua)) return false;
            return true;
        },

        disableScrollTransition(el) {
            if (!this.isIOSSafari || !el) return;
            if (el.dataset && el.dataset.noScrollTransition) return;
            el.style.transition = 'none';
            if (el.dataset) {
                el.dataset.noScrollTransition = '1';
            }
        },

        initSplitTexts() {
            const panelsContainer = this.$refs.panelsContainer;
            if (!panelsContainer) return;

            const h2Elements = panelsContainer.querySelectorAll("h2");

            h2Elements.forEach(h2 => {
                // First revert any existing SplitText instance
                if (h2._splitText) {
                    h2._splitText.revert();
                    h2._splitText = null; // Clear reference explicitly
                }

                // Create a fresh instance
                h2._splitText = new SplitText(h2, { type: "lines" });
            });
        },
        handleResize() {
            const w = window.innerWidth;
            const widthChanged = w !== this.lastResizeWidth;

            // Mobile Safari fires "resize" on scroll as the browser chrome collapses/expands.
            // SplitText only cares about width, so ignore height-only resizes.
            if (!widthChanged) {
                if (!this.isIOSSafari) {
                    this.fixedScreenHeight = window.innerHeight;
                    this.onScroll();
                }
                return;
            }

            this.lastResizeWidth = w;
            this.fixedScreenHeight = window.innerHeight;

            // Reinitialize SplitText lines after width changes
            this.initSplitTexts();
            // Update positions based on new layout
            this.onScroll();
        },

        onScroll() {
            if (this.ticking) return;

            this.ticking = true;
            window.requestAnimationFrame(() => {
                this.handleScroll();
                this.ticking = false;
            });
        },

        handleScroll() {
            const panelsContainer = this.$refs.panelsContainer;
            if (!panelsContainer) return;

            const panels = panelsContainer.querySelectorAll(".quote-panel-wrapper");
            const screenHeight = this.fixedScreenHeight || window.innerHeight;
            const smallWidth = window.innerWidth < 850;
            const val200 = 50;
            const val250 = 60;
            const val300 = 100;

            panels.forEach(panelWrapper => {
                const rect = panelWrapper.getBoundingClientRect();
                const panelMid = rect.top + rect.height / 2;
                const viewportMid = screenHeight / 2;
                let linearMultiplier = (panelMid - viewportMid) / viewportMid;
                linearMultiplier = Math.max(-1, Math.min(1, linearMultiplier));
                const easedMultiplier = Math.sign(linearMultiplier) * Math.pow(Math.abs(linearMultiplier), 3);

                const panel = panelWrapper.querySelector(".quote-panel");
                if (!panel) return;

                if (panel.classList.contains("quote-panel-special")) {
                    const svg = panel.querySelector(".tt-motif-svg");
                    if (svg) {
                        this.disableScrollTransition(svg);
                        let offsetMultiplier
                        if (smallWidth) {
                            offsetMultiplier = 0
                        } else {
                            offsetMultiplier = (easedMultiplier >= 0) ? val250 : val300;
                        }
                        svg.style.transform = `translate3d(0, ${easedMultiplier * offsetMultiplier}px, 0)`;
                    }

                    const h2 = panel.querySelector("h2");
                    if (h2 && h2._splitText) {
                        const cnt = h2._splitText.lines?.length;
                        let shift, baseShift
                        if (smallWidth) {
                            shift = 0
                            baseShift =0
                        } else {
                            shift = 0.6
                            baseShift = (easedMultiplier >= 0) ? val250 : val300;
                        }
                        h2._splitText.lines.forEach((line, idx) => {
                            this.disableScrollTransition(line);
                            let staggerPercent = (easedMultiplier >= 0) ? idx / cnt : (cnt - idx) / cnt;
                            staggerPercent = shift + (staggerPercent * 0.4);
                            const shift2 = easedMultiplier * baseShift * staggerPercent;
                            line.style.transform = `translate3d(0, ${shift2}px, 0)`;
                        });
                    }

                    const caption = panel.querySelector(".caption");
                    if (caption) {
                        this.disableScrollTransition(caption);
                        let offsetMultiplier
                        if (smallWidth) {
                            offsetMultiplier = 0
                        } else {
                            offsetMultiplier = (easedMultiplier >= 0) ? val250 : val300;
                        }

                        caption.style.transform = `translate3d(0, ${easedMultiplier * offsetMultiplier}px, 0)`;
                    }
                } else {
                    const bgPanel = panel.querySelector(".bg-panel");
                    const imgHook = panel.querySelector("img");
                    const caption = panel.querySelector(".caption");

                    // Animate caption
                    if (caption) {
                        this.disableScrollTransition(caption);
                        let m
                        if (smallWidth) {
                            m = 1
                        } else {
                            m = (easedMultiplier >= 0) ? val300 : val200;
                        }

                        caption.style.transform = `translate3d(0, ${easedMultiplier * m}px, 0)`;
                    }

                    // Animate h2 lines with gentle motion
                    const leftH2 = panel.querySelector(".quote-l2 h2");
                    if (leftH2 && leftH2._splitText) {
                        const cnt = leftH2._splitText.lines?.length;
                        // Map position to pixel shift
                        let shift
                        if (smallWidth) {
                            shift = 10
                        } else {
                            shift = 60
                        }
                        leftH2._splitText.lines.forEach((line, idx) => {
                            this.disableScrollTransition(line);
                            let staggerPercent = (easedMultiplier >= 0) ? idx / cnt : (cnt - idx) / cnt;
                            staggerPercent = 0.6 + (staggerPercent * 0.4);
                            const offsetMVal = shift * staggerPercent; // gentle motion cap
                            const shift2 = easedMultiplier * offsetMVal;
                            line.style.transform = `translate3d(0, ${shift2}px, 0)`;
                        });
                    }

                    // Animate bgPanel using clamped position
                    if (bgPanel && imgHook) {
                        this.disableScrollTransition(bgPanel);
                        const imgRect = imgHook.getBoundingClientRect();
                        const imgHeight = imgRect.height;
                        const bgHeight = bgPanel.offsetHeight;

                        let topShift, bottomShift;

                        if (smallWidth) {
                            topShift = -(imgHeight - bgHeight + 50);
                            bottomShift = 25;
                        } else {
                            topShift = -(imgHeight - bgHeight + 125);
                            bottomShift = 50;
                        }

                        const position = (panelMid - viewportMid) / viewportMid;
                        const clamped = Math.max(-1, Math.min(1, position));
                        const bgShift = topShift + (bottomShift - topShift) * ((clamped + 1) / 2);

                        bgPanel.style.transform = `translate3d(0, ${bgShift}px, 0)`;
                    }
                }
            });
        }


    }
};
</script>
